/**
 * Comissoes: o faturamento do Converte Facil (so `platform_admin`; a rota ja
 * e protegida em routes.tsx).
 *
 * Hierarquia, de cima para baixo:
 *  1. Faixa de identidade com a regra (ADR-001, E2 e E5) e a janela.
 *  2. Recorte por empresa (seletor com a contagem de cada uma; escala para
 *     centenas) e, com uma empresa escolhida, o cadastro VIGENTE dela.
 *  3. Comissao do periodo com variacao, taxa efetiva e tendencia; quatro
 *     cartoes por status que tambem filtram o extrato.
 *  4. Vendas pelo comercial (E5) e estornos x comissao revertida (E2).
 *  5. "O que fazer agora" e a projecao do mes.
 *  6. Recuperado x comissao por dia/semana/mes e o top 5 de empresas.
 *  7. Extrato: filtros combinaveis com contagem, ordenacao, paginacao, CSV
 *     do recorte inteiro, "Fechado por" ao lado do contato creditado.
 *  8. Modelo de cobranca a definir, com a simulacao de cada opcao.
 *  9. Resumo por empresa (busca, ordenacao, ver mais) e rodape.
 *
 * Decisoes nao obvias:
 *  - DOIS recursos: a visao do periodo (cartoes, blocos, grafico, projecao,
 *    resumo) e o extrato. Trocar filtro, ordem ou pagina do extrato nao
 *    recarrega o resto da tela; os dois saem das mesmas linhas no servidor.
 *  - O recorte por empresa e da administracao e viaja como `companyId`. A
 *    empresa em foco no cabecalho NAO recorta esta tela: comissao e assunto
 *    de todas as empresas, como na Central.
 *  - O estado do extrato mora AQUI para cartoes, blocos e insights abrirem o
 *    extrato ja filtrado e rolarem ate ele. Ele fica preso a um "contexto"
 *    (empresa + janela): trocar qualquer um dos dois volta para a primeira
 *    pagina, mantendo os filtros, sem um segundo fetch (o deslocamento e
 *    derivado, nao redefinido num efeito).
 *  - Percentual, modelo e politica VIGENTES vem de `useCompany().companies`
 *    (a administracao altera em Empresas -> Comissao e cobranca). O que ja
 *    foi apurado vem congelado do servidor e nunca e recalculado aqui.
 *  - Toda leitura parte de um fato ja ocorrido (checkout abandonado ha 30
 *    min, plano vencido). Nada de cobranca preventiva: sem o fato nao ha
 *    recuperacao e, portanto, nao ha comissao.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { Badge, Button, CompanyLogo, HeroBanner, Panel, Select } from '../../../components/ui';
import { ApiError, useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatCount, formatDate, formatPercentFromBps } from '../../../lib/format';
import { describeSelection, rangeDays } from '../../../lib/period';
import { usePeriod } from '../../../lib/periodContext';
import { agruparSerie, granularidadeDaJanela } from '../agrupar';
import { exportConversions, fetchCommissions, fetchConversions } from '../api';
import { CartoesComissao } from '../components/CartoesComissao';
import { EsqueletoComissoes } from '../components/EsqueletoComissoes';
import { EstornosERevertidas } from '../components/EstornosERevertidas';
import { ExtratoDeConversoes } from '../components/ExtratoDeConversoes';
import { GraficoRecuperadoComissao, UNIDADE_LABEL } from '../components/GraficoRecuperadoComissao';
import { ModeloDeCobranca } from '../components/ModeloDeCobranca';
import { OQueFazerAgora } from '../components/OQueFazerAgora';
import { ProjecaoDoMes } from '../components/ProjecaoDoMes';
import { ResumoPorEmpresa } from '../components/ResumoPorEmpresa';
import { TopEmpresas } from '../components/TopEmpresas';
import { VendasPeloComercial } from '../components/VendasPeloComercial';
import { baixarArquivoTexto, conversoesParaCsv, nomeDoArquivoCsv } from '../csv';
import { alternarOrdem, ESTADO_INICIAL_DO_EXTRATO, FILTROS_VAZIOS, type EstadoDaExportacao, type EstadoDoExtrato } from '../extrato';
import { gerarInsights, PARAM_ABA_DA_EMPRESA, type AcaoDoInsight } from '../insights';
import { POLITICA_PADRAO } from '../regras';
import type { ClosedByFilter, ConversionFilters, ConversionQuery, ConversionStatus, RefundFilter, SortColumn } from '../types';
import { MODELO_COBRANCA, POLITICA_ESTORNO, plural } from '../vocabulario';

const TODAS = 'todas';
const TODAS_AS_EMPRESAS = 'Todas as empresas';
const AVISO_VISIVEL_MS = 4_000;

/**
 * Rola ate a secao. Os alvos levam `scroll-mt-20`: o cabecalho do painel e
 * fixo (sticky) e, sem a margem, o titulo da secao pararia escondido atras
 * dele. Quem pediu menos movimento no sistema nao ganha rolagem animada.
 */
function rolarAte(id: string): void {
  const reduzir = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.getElementById(id)?.scrollIntoView({ behavior: reduzir ? 'auto' : 'smooth', block: 'start' });
}

export function AdminComissoes(): JSX.Element {
  const { companies, selectCompany } = useCompany();
  const { range, selection } = usePeriod();
  const navigate = useNavigate();
  const location = useLocation();
  const idEmpresa = useId();

  useEffect(() => {
    document.title = 'Comissões · Converte Fácil';
  }, []);

  // Recorte por empresa. Um id que saiu do cadastro volta para "todas" sem estado invalido.
  const [filtro, setFiltro] = useState<string>(TODAS);
  const empresaFiltrada = companies.find((c) => c.id === filtro) ?? null;
  const companyId = empresaFiltrada === null ? null : empresaFiltrada.id;
  const escopo = empresaFiltrada === null ? TODAS_AS_EMPRESAS : empresaFiltrada.name;
  const empresasDoRecorte = useMemo(() => (companyId === null ? companies : companies.filter((c) => c.id === companyId)), [companies, companyId]);

  // Visao do periodo.
  const buscarVisao = useCallback((signal: AbortSignal) => fetchCommissions(range, companyId, signal), [range, companyId]);
  const visao = useApiResource(buscarVisao, 60_000);

  // Extrato preso ao contexto (ver cabecalho).
  const contexto = `${companyId ?? TODAS}|${range.from ?? ''}|${range.to ?? ''}`;
  const [extrato, setExtrato] = useState<{ readonly contexto: string; readonly estado: EstadoDoExtrato }>({ contexto, estado: ESTADO_INICIAL_DO_EXTRATO });
  const estado = extrato.estado;
  const offset = extrato.contexto === contexto ? estado.offset : 0;

  const atualizarExtrato = useCallback(
    (patch: Partial<EstadoDoExtrato>): void => {
      setExtrato((atual) => {
        const base = atual.contexto === contexto ? atual.estado : { ...atual.estado, offset: 0 };
        const mudouRecorte = Object.keys(patch).some((chave) => chave !== 'offset');
        return { contexto, estado: { ...base, ...patch, offset: patch.offset ?? (mudouRecorte ? 0 : base.offset) } };
      });
    },
    [contexto],
  );
  const ordenarExtrato = useCallback((coluna: SortColumn): void => atualizarExtrato(alternarOrdem(estado, coluna)), [atualizarExtrato, estado]);
  const limparExtrato = useCallback((): void => atualizarExtrato({ ...FILTROS_VAZIOS }), [atualizarExtrato]);

  const consulta = useMemo<ConversionQuery>(() => ({ ...estado, companyId, offset }), [estado, companyId, offset]);
  const buscarExtrato = useCallback((signal: AbortSignal) => fetchConversions(range, consulta, signal), [range, consulta]);
  const conversoes = useApiResource(buscarExtrato, 60_000);

  // Exportacao do recorte inteiro.
  const [exportacao, setExportacao] = useState<EstadoDaExportacao>({ status: 'parado' });
  const exportando = useRef<AbortController | null>(null);
  useEffect(() => () => exportando.current?.abort(), []);
  useEffect(() => {
    if (exportacao.status !== 'pronto' && exportacao.status !== 'erro') {
      return undefined;
    }
    const timer = window.setTimeout(() => setExportacao({ status: 'parado' }), AVISO_VISIVEL_MS);
    return () => window.clearTimeout(timer);
  }, [exportacao]);

  const exportar = (): void => {
    exportando.current?.abort();
    const controller = new AbortController();
    exportando.current = controller;
    setExportacao({ status: 'gerando' });
    exportConversions(range, consulta, controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) {
          return;
        }
        baixarArquivoTexto(nomeDoArquivoCsv(escopo, new Date()), conversoesParaCsv(envelope.data.rows));
        setExportacao({ status: 'pronto', linhas: envelope.data.rows.length, total: envelope.data.total, truncado: envelope.data.truncated });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setExportacao({ status: 'erro', mensagem: error instanceof ApiError ? error.userMessage : 'Não foi possível gerar o CSV.' });
      });
  };

  // Atalhos: cartoes, blocos e insights filtram o extrato e rolam ate ele.
  const filtrarExtrato = useCallback(
    (filtros: Partial<ConversionFilters>): void => {
      atualizarExtrato(filtros);
      rolarAte('extrato');
    },
    [atualizarExtrato],
  );
  const alternarStatus = (status: ConversionStatus): void => filtrarExtrato({ status: estado.status === status ? 'todos' : status });
  const alternarFechadoPor = (f: ClosedByFilter): void => filtrarExtrato({ closedBy: estado.closedBy === f ? 'todos' : f });
  const alternarEstorno = (f: RefundFilter): void => filtrarExtrato({ refund: estado.refund === f ? 'todos' : f });

  const filtrarEmpresa = useCallback((id: string | null): void => {
    setFiltro(id ?? TODAS);
    rolarAte('recorte');
  }, []);

  const abrirComercial = useCallback(
    (id: string): void => {
      selectCompany(id);
      navigate({ pathname: '/comercial', search: location.search });
    },
    [selectCompany, navigate, location.search],
  );

  const executarAcao = (acao: AcaoDoInsight): void => {
    switch (acao.kind) {
      case 'extrato':
        filtrarExtrato({ ...FILTROS_VAZIOS, ...acao.filtros });
        return;
      case 'ancora':
        rolarAte(acao.id);
        return;
      case 'rota': {
        // Mantem o periodo da URL e, quando a acao pede, abre direto na aba certa do detalhe.
        const params = new URLSearchParams(location.search);
        if (acao.aba !== null) {
          params.set(PARAM_ABA_DA_EMPRESA, acao.aba);
        }
        const search = params.toString();
        navigate({ pathname: acao.rota, search: search === '' ? '' : `?${search}` });
        return;
      }
    }
  };

  const dados = visao.state.status === 'success' ? visao.state.data : null;
  const dias = rangeDays(range);
  const serie = useMemo(() => (dados === null ? null : agruparSerie(dados.series, granularidadeDaJanela(dias))), [dados, dias]);
  const insights = useMemo(() => (dados === null ? [] : gerarInsights(dados, companies)), [dados, companies]);
  const periodoTexto = describeSelection(selection);
  const atualizando = (visao.state.status === 'success' && visao.state.refreshing) || (conversoes.state.status === 'success' && conversoes.state.refreshing);

  const contagemDa = (id: string): number | null => (dados === null ? null : (dados.companyFacets.find((f) => f.companyId === id)?.conversionCount ?? 0));
  const contagemTotal = dados === null ? null : dados.companyFacets.reduce((acc, f) => acc + f.conversionCount, 0);

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="comissao"
        title="Comissões"
        badges={
          <>
            <Badge tone="brand" dot>
              Administração
            </Badge>
            <Badge tone="neutral" icon="escudo">
              Só depois do fato
            </Badge>
          </>
        }
        description="Faturamento do Converte Fácil. Conta a venda paga até 7 dias depois de um toque do motor, creditada ao último toque e pelo valor pago, mesmo quando a vendedora fecha. Reembolso não reverte; chargeback reverte, conforme a política de cada empresa (ADR-001)."
        aside={
          <>
            <RefreshingDot active={atualizando} />
            <Badge tone="neutral" icon="calendario">
              {periodoTexto}
            </Badge>
          </>
        }
      />

      <Panel className="flex flex-wrap items-end gap-x-6 gap-y-3 px-5 py-4">
        <div id="recorte" className="flex w-full scroll-mt-24 flex-col gap-1 sm:w-80">
          <label htmlFor={idEmpresa} className="text-[11px] font-medium text-ink-subtle">
            Empresa · recorta a página inteira
          </label>
          <Select
            id={idEmpresa}
            size="sm"
            value={companyId ?? TODAS}
            onChange={(event) => setFiltro(event.target.value)}
          >
            <option value={TODAS}>
              {TODAS_AS_EMPRESAS}
              {contagemTotal === null ? '' : ` (${formatCount(contagemTotal)})`}
            </option>
            {companies.map((c) => {
              const n = contagemDa(c.id);
              return (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {n === null ? '' : ` (${formatCount(n)})`}
                </option>
              );
            })}
          </Select>
        </div>
        {empresaFiltrada === null ? (
          <p className="pb-1.5 text-xs text-ink-subtle">
            {formatCount(companies.length)} {plural(companies.length, 'empresa', 'empresas')} no cadastro. O número entre parênteses é de conversões no período.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2 pb-0.5">
            <CompanyLogo company={empresaFiltrada} size="sm" />
            <span className="text-sm font-semibold text-ink">{empresaFiltrada.name}</span>
            <Badge tone="brand">{formatPercentFromBps(empresaFiltrada.feeBps)} vigente</Badge>
            <Badge tone={MODELO_COBRANCA[empresaFiltrada.billingModel].tone}>{MODELO_COBRANCA[empresaFiltrada.billingModel].label}</Badge>
            <Badge tone="neutral">Estorno: {POLITICA_ESTORNO[empresaFiltrada.refundPolicy ?? POLITICA_PADRAO].curto}</Badge>
            <Button variant="ghost" size="sm" icon="x" onClick={() => setFiltro(TODAS)}>
              {TODAS_AS_EMPRESAS}
            </Button>
          </div>
        )}
      </Panel>

      {visao.state.status === 'loading' ? <EsqueletoComissoes /> : null}

      {visao.state.status === 'error' ? <ErrorPanel title="Não foi possível carregar as comissões" error={visao.state.error} onRetry={visao.reload} /> : null}

      {dados !== null && serie !== null ? (
        <>
          {visao.state.status === 'success' && visao.state.simulated ? (
            <SimulatedNotice>
              Extrato fictício e fixo, gerado a partir do catálogo de empresas, ofertas e vendedoras da prévia. Nenhum valor aqui vem do banco nem cobra ninguém.
            </SimulatedNotice>
          ) : null}

          <CartoesComissao
            summary={dados.summary}
            previous={dados.previous}
            tendencia={serie.comissaoCents}
            rotuloTendencia={`Comissão por ${UNIDADE_LABEL[serie.granularidade]} no período`}
            periodo={periodoTexto}
            escopo={escopo}
            statusAtivo={estado.status}
            onStatus={alternarStatus}
          />

          <div className="grid gap-6 lg:grid-cols-2">
            <VendasPeloComercial
              comercial={dados.summary.comercial}
              previous={dados.previous}
              totalComissaoCents={dados.summary.commissionCents}
              companies={companies}
              filtroAtivo={estado.closedBy}
              onVerNoExtrato={alternarFechadoPor}
              onAbrirComercial={abrirComercial}
            />
            <EstornosERevertidas summary={dados.summary} previous={dados.previous} empresas={empresasDoRecorte} filtroAtivo={estado.refund} onVerNoExtrato={alternarEstorno} />
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <OQueFazerAgora insights={insights} onAcao={executarAcao} className="lg:col-span-2" />
            <ProjecaoDoMes mes={dados.month} rows={dados.companies} empresas={companies} />
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <GraficoRecuperadoComissao serie={serie} escopo={escopo} className="lg:col-span-2" />
            <TopEmpresas
              rows={dados.companies}
              companies={companies}
              totalComissaoCents={dados.summary.commissionCents}
              empresaFiltrada={companyId}
              onFiltrar={filtrarEmpresa}
              onVerTodas={() => rolarAte('resumo-por-empresa')}
            />
          </div>

          <ExtratoDeConversoes
            state={conversoes.state}
            onRetry={conversoes.reload}
            estado={{ ...estado, offset }}
            onEstado={atualizarExtrato}
            onOrdenar={ordenarExtrato}
            onLimpar={limparExtrato}
            companies={companies}
            empresaFiltrada={empresaFiltrada}
            onTirarEmpresa={() => setFiltro(TODAS)}
            exportacao={exportacao}
            onExportar={exportar}
          />

          <ModeloDeCobranca summary={dados.summary} empresas={empresasDoRecorte} mesAtual={dados.month.monthKey} />

          <ResumoPorEmpresa rows={dados.companies} companies={companies} empresaFiltrada={companyId} onFiltrar={filtrarEmpresa} />

          <p className="text-xs text-ink-subtle">
            Período de {formatDate(dados.periodStart)} a {formatDate(dados.periodEnd)} ({periodoTexto}) · {escopo}
            {dados.previousPeriod === null
              ? ' · sem período anterior para comparar'
              : ` · comparado com ${formatDate(dados.previousPeriod.start)} a ${formatDate(dados.previousPeriod.end)}, o período anterior de mesmo tamanho`}
            {' · '}A comissão existe porque recuperamos quem já tinha saído: nenhum toque acontece antes do abandono ou do vencimento.
          </p>
        </>
      ) : null}
    </section>
  );
}
